import React, { useMemo } from 'react';
import {
  MdGrade, MdEventAvailable, MdPayments, MdNotificationsNone, MdFamilyRestroom,
} from 'react-icons/md';
import { Card, StatCard, Badge, EmptyState } from '../../ui/Card';
import { monthLabel, shortDate, money } from '../../ui/format';
import { useStudentData } from '../StudentDataContext';
import { childStats, rateTone, feesTone, feesWord, firstName } from '../childStats';
import './pages.css';

/** Parents of 2+ pupils: every child side by side, tap one to open it. */
function FamilyOverview() {
  const { family, currentId, selectChild } = useStudentData();
  return (
    <Card title="أبنائي" icon={MdFamilyRestroom} padded={false}>
      <div className="kids-grid">
        {family.map((c) => {
          const s = childStats(c);
          const active = c.id === currentId;
          return (
            <button
              key={c.id}
              type="button"
              className={`kid-card${active ? ' is-active' : ''}`}
              onClick={() => selectChild(c.id)}
              aria-pressed={active}
            >
              <div className="kid-card-head">
                <span className={`kid-avatar is-${c.gender || 'none'}`}>{firstName(c).charAt(0)}</span>
                <div className="kid-card-who">
                  <strong>{c.name}</strong>
                  <span>{c.section_label || '—'}</span>
                </div>
                {c.unread > 0 && <Badge tone="danger">{c.unread}</Badge>}
              </div>
              <div className="kid-metrics">
                <span className="kid-metric tone-primary">
                  <b>{s.average === null ? '—' : `${s.average}%`}</b><small>المعدل</small>
                </span>
                <span className={`kid-metric tone-${rateTone(s.rate)}`}>
                  <b>{s.rate === null ? '—' : `${s.rate}%`}</b><small>الحضور</small>
                </span>
                <span className={`kid-metric tone-${feesTone(s)}`}>
                  <b>{feesWord(s)}</b><small>القسط</small>
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

export default function Home() {
  const { grades, attendance, fees, notifications, hasSiblings } = useStudentData();

  const stats = useMemo(() => childStats({ grades, attendance, fees }), [grades, attendance, fees]);
  const recent = notifications.slice(0, 5);

  return (
    <div className="page">
      {hasSiblings && <FamilyOverview />}

      <div className="stat-grid">
        <StatCard
          icon={MdGrade}
          tone="primary"
          value={stats.average === null ? '—' : `${stats.average}%`}
          label="المعدل العام"
          hint={stats.examCount ? `${stats.examCount} امتحان` : 'لا توجد علامات بعد'}
        />
        <StatCard
          icon={MdEventAvailable}
          tone={rateTone(stats.rate)}
          value={stats.rate === null ? '—' : `${stats.rate}%`}
          label="نسبة الحضور"
          hint={stats.absences ? `${stats.absences} غياب` : 'لا غياب'}
        />
        <StatCard
          icon={MdPayments}
          tone={feesTone(stats)}
          value={feesWord(stats)}
          label="قسط هذا الشهر"
          hint={stats.feesNone ? 'لم يُحدَّد قسط'
            : fees?.paid_through_month ? `مدفوع حتى ${monthLabel(fees.paid_through_month)}`
            : 'لم تُسجَّل دفعة'}
        />
        <StatCard
          icon={MdNotificationsNone}
          tone="info"
          value={notifications.filter((n) => !n.is_read).length}
          label="إشعارات جديدة"
          hint={notifications.length ? `${notifications.length} إجمالاً` : 'لا توجد إشعارات'}
        />
      </div>

      <Card title="أحدث الإشعارات" icon={MdNotificationsNone} padded={false}>
        {recent.length === 0 ? (
          <EmptyState icon={MdNotificationsNone} title="لا توجد إشعارات" hint="ستظهر هنا رسائل المدرسة." />
        ) : (
          <ul className="row-list">
            {recent.map((n) => (
              <li key={n.id} className="row">
                <div className="row-main">
                  <div className="row-title">
                    {n.title}
                    {!n.is_read && <Badge tone="primary">جديد</Badge>}
                  </div>
                  <p className="row-sub">{n.message}</p>
                </div>
                <span className="row-meta">{shortDate(n.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {fees && fees.remaining_cents > 0 && !stats.feesExempt && (
        <div className="notice notice-warning">
          المبلغ المتبقي من الأقساط: <strong>{money(fees.remaining_cents)}</strong>
        </div>
      )}
    </div>
  );
}
